import { useState, useEffect } from "react"
import { useData } from "@/lib/data-context"
import { getMonthDays, formatDate, groupDaysByWeek, getMonthName, isToday } from "@/lib/date-utils"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { WorkStatus } from "@/lib/types"
import { greekHolidays } from "@/lib/holidays"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ENABLE_CHRISTMAS_THEME, ENABLE_VACATION_THEME } from "@/config/theme"
import { VacationEffects } from "@/components/vacation-effects"

/**
 * AdminDashboard Component
 *
 * Displays all employees' work schedules for the admin view.
 * Allows filtering by employee and status, viewing team-wide statistics.
 */
export function AdminDashboard() {
  const { employees, workDays, loading, getWorkStatus, getEmployeeStats, refreshWorkDays, refreshEmployees } = useData()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedUserId, setSelectedUserId] = useState<string>("all")
  const [selectedStatus, setSelectedStatus] = useState<string>("all")
  

  

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const monthDays = getMonthDays(year, month)
  const weeks = groupDaysByWeek(monthDays)

  // Fetch employees once on mount
  useEffect(() => {
    refreshEmployees()
  }, [refreshEmployees])

  // Fetch work days when month or filters change
  useEffect(() => {
    const filters: { status?: WorkStatus; userId?: string } = {}
    
    if (selectedStatus !== "all") {
      filters.status = selectedStatus as WorkStatus
    }
    
    if (selectedUserId !== "all") {
      filters.userId = selectedUserId
    }
    
    refreshWorkDays(year, month, Object.keys(filters).length > 0 ? filters : undefined)
  }, [year, month, selectedStatus, selectedUserId, refreshWorkDays])

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  /**
   * Gets all employees for a date, grouped by status
   */
  const getEmployeesByStatus = (date: Date) => {
    const dateStr = formatDate(date)
    
    const byStatus: Record<WorkStatus, typeof employees> = {
      office: [],
      remote: [],
      day_off: []
    }

    let employeesToCheck = employees
    
    // Apply employee filter
    if (selectedUserId !== "all") {
      employeesToCheck = employees.filter((emp) => emp.id === selectedUserId)
    }

    employeesToCheck.forEach((emp) => {
      const status = getWorkStatus(emp.id, dateStr)
      byStatus[status].push(emp)
    })

    // Apply status filter
    if (selectedStatus !== "all") {
      const filterStatus = selectedStatus as WorkStatus
      return {
        [filterStatus]: byStatus[filterStatus]
      }
    }

    return byStatus
  }

  const allEmployees = employees

  // Generate snowflakes (only if Christmas theme is enabled)
  const snowflakes = ENABLE_CHRISTMAS_THEME
    ? Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        animationDuration: Math.random() * 3 + 2 + 's',
        animationDelay: Math.random() * 5 + 's',
        fontSize: Math.random() * 10 + 10 + 'px',
      }))
    : []

  return (
    <div className={cn(
      "min-h-screen relative",
      ENABLE_CHRISTMAS_THEME && "christmas-bg",
      ENABLE_VACATION_THEME && "vacation-bg"
    )}>
      {/* Snowflakes */}
      {ENABLE_CHRISTMAS_THEME && snowflakes.map((snowflake) => (
        <div
          key={snowflake.id}
          className="snowflake"
          style={{
            left: `${snowflake.left}%`,
            animationDuration: snowflake.animationDuration,
            animationDelay: snowflake.animationDelay,
            fontSize: snowflake.fontSize,
          }}
        />
      ))}

      {ENABLE_VACATION_THEME && <VacationEffects />}
      
      <div className="container mx-auto p-6 max-w-7xl relative z-10">
        <div className="mb-8">
        <h1 className={cn(
          "text-3xl font-bold mb-2",
          ENABLE_CHRISTMAS_THEME && "text-white drop-shadow-lg",
          ENABLE_VACATION_THEME && "text-sky-950 drop-shadow"
        )}>
          {ENABLE_CHRISTMAS_THEME
            ? "🎄 Admin Dashboard 🎄"
            : ENABLE_VACATION_THEME
              ? "🏖️ Admin Dashboard ☀️"
              : "Admin Dashboard"}
        </h1>
        <p className={cn(
          "text-muted-foreground",
          ENABLE_CHRISTMAS_THEME && "text-white/90 drop-shadow",
          ENABLE_VACATION_THEME && "text-sky-900/85"
        )}>
          View all employees' remote work schedules
        </p>
        
        {/* Debug info - remove in production */}
        <div className="mt-2 flex items-center gap-4 text-xs">
          <span className={cn(
            ENABLE_CHRISTMAS_THEME
              ? "text-white/80"
              : ENABLE_VACATION_THEME
                ? "text-sky-900/75"
                : "text-muted-foreground"
          )}>
            📊 {employees.length} employees · {workDays.length} work day records
          </span>
          {loading && <span className="flex items-center gap-1 text-blue-600">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
            Loading from API...
          </span>}
        </div>
      </div>

      <Card className={cn(
        "p-6",
        ENABLE_CHRISTMAS_THEME && "bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-2 border-red-200 dark:border-red-800 shadow-lg",
        ENABLE_VACATION_THEME && "bg-transparent border-transparent shadow-none"
      )}>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className={cn(
            "text-2xl font-semibold",
            ENABLE_CHRISTMAS_THEME && "text-red-600 dark:text-red-400",
            ENABLE_VACATION_THEME && "text-sky-600 dark:text-sky-400"
          )}>
            {ENABLE_CHRISTMAS_THEME
              ? `🎅 ${getMonthName(month)} ${year} 🎅`
              : ENABLE_VACATION_THEME
                ? `🌊 ${getMonthName(month)} ${year} ⛱️`
                : `${getMonthName(month)} ${year}`}
          </h2>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">Filter by Employee</label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {allEmployees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">Filter by Status</label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="remote">Remote Only</SelectItem>
                <SelectItem value="day_off">Day Off Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          {/* Day headers */}
          <div className="grid grid-cols-5 gap-2">
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((day) => (
              <div key={day} className="text-center font-semibold text-sm py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar weeks */}
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-5 gap-2">
              {week.map((day, dayIndex) => {
                // Handle empty cells (days outside the month or week)
                if (!day) {
                  return <div key={`empty-${weekIndex}-${dayIndex}`} className="min-h-[140px]" />
                }
                
                const employeesByStatus = getEmployeesByStatus(day)
                const dayOfMonth = day.getUTCDate()
                const today = isToday(day)
                const dateStr = formatDate(day)
                const holiday = greekHolidays[dateStr]
                
                const hasAnyEmployees = Object.values(employeesByStatus).some(list => list.length > 0)

                const dayCell = (
                  <div
                    key={dateStr}
                    className={cn(
                      "min-h-[140px] p-3 rounded-lg border-2 transition-colors",
                      !holiday && (
                        hasAnyEmployees
                          ? ENABLE_CHRISTMAS_THEME
                            ? "border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-900/20"
                            : ENABLE_VACATION_THEME
                              ? "border-sky-300/80 dark:border-sky-600 bg-white/25 dark:bg-sky-900/20 backdrop-blur-[2px]"
                              : "border-primary/30 bg-primary/5"
                          : ENABLE_CHRISTMAS_THEME
                            ? "border-border bg-white/80 dark:bg-gray-800/80"
                            : ENABLE_VACATION_THEME
                              ? "border-white/50 bg-white/20 backdrop-blur-[2px]"
                              : "border-border bg-card"
                      ),
                      today && (
                        ENABLE_VACATION_THEME
                          ? "ring-4 ring-yellow-300 ring-offset-2 ring-offset-transparent shadow-lg"
                          : "ring-4 ring-yellow-400 ring-offset-2 ring-offset-background shadow-lg"
                      ),
                      holiday && (
                        ENABLE_CHRISTMAS_THEME
                          ? "bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700"
                          : ENABLE_VACATION_THEME
                            ? "bg-sky-500/35 border-sky-400/70 text-sky-950 backdrop-blur-[2px]"
                            : "bg-muted border-border"
                      )
                    )}
                  >
                    <div className="font-semibold text-lg mb-2">{dayOfMonth}</div>
                    
                    {holiday ? (
                      <div className="text-muted-foreground font-semibold">{holiday}</div>
                    ) : hasAnyEmployees ? (
                      <div className="space-y-2">
                        {/* Remote employees */}
                        {employeesByStatus.remote && employeesByStatus.remote.length > 0 && (
                          <div className="space-y-1">
                            {employeesByStatus.remote.map((employee) => (
                              <div
                                key={employee.id}
                                className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 border border-blue-500/30 text-blue-700 dark:text-blue-300 truncate"
                                title={`${employee.name} - Remote`}
                              >
                                {employee.name}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Day off employees */}
                        {employeesByStatus.day_off && employeesByStatus.day_off.length > 0 && (
                          <div className="space-y-1">
                            {employeesByStatus.day_off.map((employee) => (
                              <div
                                key={employee.id}
                                className="px-2 py-1 rounded text-xs font-medium bg-orange-500/20 border border-orange-500/30 text-orange-700 dark:text-orange-300 truncate"
                                title={`${employee.name} - Day Off`}
                              >
                                {employee.name}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Office employees (only show when filtering by office or all) */}
                        {employeesByStatus.office && employeesByStatus.office.length > 0 && selectedStatus === "office" && (
                          <div className="space-y-1">
                            {employeesByStatus.office.map((employee) => (
                              <div
                                key={employee.id}
                                className="px-2 py-1 rounded text-xs font-medium bg-muted border border-border truncate"
                                title={`${employee.name} - Office`}
                              >
                                {employee.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground italic">
                        {selectedStatus === "all" ? "All in office" : "None"}
                      </div>
                    )}
                  </div>
                )

                if (holiday) {
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>{dayCell}</TooltipTrigger>
                      <TooltipContent>
                        <p>{holiday}</p>
                      </TooltipContent>
                    </Tooltip>
                  )
                }

                return dayCell
              })}
            </div>
          ))}
        </div>
      </Card>

      <Card className={cn(
        "mt-6 p-6",
        ENABLE_CHRISTMAS_THEME && "bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-2 border-green-200 dark:border-green-800 shadow-lg",
        ENABLE_VACATION_THEME && "bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-2 border-amber-200 dark:border-amber-700 shadow-lg"
      )}>
        <h3 className="font-semibold mb-4">
          {selectedUserId === "all" ? "All Employees" : "Selected Employee"} - Monthly Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {allEmployees
            .filter((emp) => selectedUserId === "all" || emp.id === selectedUserId)
            .map((employee) => {
              const stats = getEmployeeStats(employee.id, year, month)

              return (
                <div key={employee.id} className="p-4 rounded-lg border bg-card">
                  <div className="font-medium mb-2">{employee.name}</div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
                      <span className="text-muted-foreground">Remote:</span>
                      <span className="font-medium">{stats.remote} days</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="inline-block w-3 h-3 rounded-full bg-orange-500"></span>
                      <span className="text-muted-foreground">Day Off:</span>
                      <span className="font-medium">{stats.dayOff} days</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="inline-block w-3 h-3 rounded-full bg-muted"></span>
                      <span className="text-muted-foreground">Office:</span>
                      <span className="font-medium">{stats.office} days</span>
                    </div>
                  </div>
                </div>
              )
            })}
        </div>
      </Card>
      </div>
    </div>
  )
}
